import React, { useEffect, useState } from "react";
import { X, Smartphone, Share, Plus, Download, MoreVertical, Apple } from "lucide-react";
import { isStandalone, detectPlatform } from "@/lib/pwa";

const DISMISS_KEY = "fliboe_install_dismissed_at";
const DISMISS_DAYS = 7;

function shouldAutoShow() {
  if (isStandalone()) return false;
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return true;
    const last = Number(v);
    if (!Number.isFinite(last)) return true;
    const days = (Date.now() - last) / (1000 * 60 * 60 * 24);
    return days > DISMISS_DAYS;
  } catch (_) {
    return true;
  }
}

export default function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("desktop");
  const [installable, setInstallable] = useState(!!(typeof window !== "undefined" && window.__fliboeInstallEvent));

  useEffect(() => {
    setPlatform(detectPlatform());
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => { setInstallable(false); setOpen(false); };
    const onOpenInstall = () => setOpen(true);
    window.addEventListener("fliboe:installable", onInstallable);
    window.addEventListener("fliboe:installed", onInstalled);
    window.addEventListener("fliboe:open-install", onOpenInstall);
    // Auto-show after a short delay on first eligible visit
    const t = setTimeout(() => {
      if (shouldAutoShow()) setOpen(true);
    }, 6000);
    return () => {
      window.removeEventListener("fliboe:installable", onInstallable);
      window.removeEventListener("fliboe:installed", onInstalled);
      window.removeEventListener("fliboe:open-install", onOpenInstall);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setOpen(false);
  };

  const triggerNativeInstall = async () => {
    const evt = window.__fliboeInstallEvent;
    if (!evt) return;
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      if (choice && choice.outcome === "accepted") {
        setInstallable(false);
        setOpen(false);
      }
    } catch (_) {}
    window.__fliboeInstallEvent = null;
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
      data-testid="install-prompt-overlay"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0a0a0a] border border-emerald-500/30 rounded-2xl shadow-2xl shadow-emerald-500/10 overflow-hidden"
        data-testid="install-prompt"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <Smartphone className="w-5 h-5 text-emerald-500" />
            <span className="fl-display text-lg font-bold tracking-tight">Install Fliboe</span>
          </div>
          <button
            onClick={dismiss}
            className="text-slate-400 hover:text-white"
            aria-label="Dismiss"
            data-testid="install-dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-slate-300">
            Get the full-screen Fliboe experience on your home screen — faster launch,
            looks like a native app, and works even when the browser is closed.
          </p>

          {platform === "ios" && (
            <ol className="space-y-3 text-sm" data-testid="install-steps-ios">
              <li className="flex gap-3 items-start">
                <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">1</span>
                <span>Tap the <Share className="inline w-4 h-4 mx-1 -mt-0.5" /> <span className="font-semibold">Share</span> button at the bottom of Safari.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">2</span>
                <span>Scroll down and tap <Plus className="inline w-4 h-4 mx-1 -mt-0.5" /> <span className="font-semibold">Add to Home Screen</span>.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">3</span>
                <span>Tap <span className="font-semibold">Add</span> in the top-right. Fliboe will appear on your home screen.</span>
              </li>
              <li className="pt-2 flex items-center gap-2 text-[11px] fl-mono uppercase tracking-widest text-slate-500">
                <Apple className="w-3.5 h-3.5" /> iOS · Safari only
              </li>
            </ol>
          )}

          {platform === "android" && (
            <>
              {installable ? (
                <div className="space-y-3" data-testid="install-steps-android-native">
                  <p className="text-sm text-slate-400">Tap the button below to install instantly.</p>
                  <button
                    onClick={triggerNativeInstall}
                    className="fl-btn w-full !py-3 text-sm"
                    data-testid="install-native-android"
                  >
                    <Download className="w-4 h-4" /> Install Fliboe app
                  </button>
                </div>
              ) : (
                <ol className="space-y-3 text-sm" data-testid="install-steps-android">
                  <li className="flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">1</span>
                    <span>Tap the <MoreVertical className="inline w-4 h-4 mx-1 -mt-0.5" /> <span className="font-semibold">menu</span> in the top-right of Chrome.</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">2</span>
                    <span>Tap <span className="font-semibold">Install app</span> (or "Add to Home screen").</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">3</span>
                    <span>Confirm <span className="font-semibold">Install</span>. Fliboe will live in your app drawer.</span>
                  </li>
                </ol>
              )}
            </>
          )}

          {platform === "desktop" && (
            <>
              {installable ? (
                <button
                  onClick={triggerNativeInstall}
                  className="fl-btn w-full !py-3 text-sm"
                  data-testid="install-native-desktop"
                >
                  <Download className="w-4 h-4" /> Install Fliboe
                </button>
              ) : (
                <ol className="space-y-3 text-sm" data-testid="install-steps-desktop">
                  <li className="flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">1</span>
                    <span>Look for the <Download className="inline w-4 h-4 mx-1 -mt-0.5" /> install icon in your browser's address bar.</span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 fl-mono text-xs flex items-center justify-center">2</span>
                    <span>Click it, then choose <span className="font-semibold">Install</span>. Fliboe opens in its own window from now on.</span>
                  </li>
                </ol>
              )}
            </>
          )}

          <button
            onClick={dismiss}
            className="w-full text-center fl-mono text-[11px] tracking-widest uppercase text-slate-500 hover:text-slate-300 pt-2"
            data-testid="install-maybe-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
